import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import './Services.css';

function Services() {
  const navigate = useNavigate();

  const handleBookingClick = () => {
    navigate('/serviceBooking');
  };

  // Optimized services data for maximum engagement & SEO
  const servicesData = [
    { title: 'Website Development', desc: 'Responsive, SEO-friendly websites with modern UI/UX. Perfect for businesses, portfolios, and e-commerce stores.' },
    { title: 'Mobile App Development', desc: 'Custom iOS, Android & cross-platform apps designed for performance, engagement, and scalability.' },
    { title: 'AI & Machine Learning Solutions', desc: 'Implement AI/ML algorithms, predictive analytics, and smart automation for your business.' },
    { title: 'Chatbot Development', desc: 'AI-driven chatbots for 24/7 customer support, lead generation, and engagement on web and mobile.' },
    { title: 'E-commerce Solutions', desc: 'Fully functional online stores with payment gateways, order tracking, inventory, and marketing tools.' },
    { title: 'SEO & Digital Marketing', desc: 'Boost your online visibility with SEO, content marketing, social media campaigns, and paid ads.' },
    { title: 'Cloud & DevOps', desc: 'Cloud migration, CI/CD pipelines, infrastructure automation, and scalable architecture.' },
    { title: 'UI/UX Design', desc: 'Engaging, intuitive, and visually appealing interfaces to enhance user retention and satisfaction.' },
    { title: 'API Integration', desc: 'Seamlessly integrate payment gateways, CRMs, logistics, and analytics for automation and efficiency.' },
    { title: 'Cybersecurity Services', desc: 'Protect your business with penetration testing, vulnerability scanning, and secure infrastructure.' },
    { title: 'Data Management & Analytics', desc: 'Clean, structure, and analyze your data to generate actionable business insights.' },
    { title: 'Custom Software Development', desc: 'Tailored software solutions including ERP, CRM, inventory, and workflow management systems.' },
    { title: 'Game Development', desc: 'Build interactive 2D & 3D games for web, mobile, and desktop with stunning graphics.' },
    { title: 'Cloud Hosting & Domain Management', desc: 'Reliable hosting, SSL setup, domain management, and server optimization for your website.' },
    { title: 'Technical Consultation', desc: 'Expert guidance on technology stack, system architecture, and digital transformation strategies.' },
    { title: 'Dataset Creation & Annotation', desc: 'High-quality datasets for AI/ML applications with accuracy and proper labeling.' },
    { title: 'IT Lab Setup for Education', desc: 'Design and implement modern IT labs for schools, colleges, and training centers.' },
    { title: 'Hardware Maintenance & Support', desc: 'Repair, maintain, and optimize computers, servers, and IT equipment for smooth operations.' }
  ];

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    provider: {
      "@type": "Organization",
      name: "Samsoftix",
      url: "https://www.samsoftix.com"
    },
    serviceType: servicesData.map(s => s.title),
    description: "Samsoftix offers professional digital services including website & app development, AI/ML solutions, SEO, e-commerce, cloud hosting, and IT consulting to help businesses grow online."
  };

  // Fade-in effect on scroll
  useEffect(() => {
    const sections = document.querySelectorAll('.fade-section');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = 1;
            entry.target.style.transform = 'translateY(0)';
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    sections.forEach(section => observer.observe(section));
  }, []);

  return (
    <main className="services-page">
      <Helmet>
        <title>Digital Services & Solutions | Samsoftix</title>
        <meta
          name="description"
          content="Samsoftix provides top digital services including website & app development, AI/ML, SEO, e-commerce, cloud hosting, and IT consulting. Book online today!"
        />
        <meta
          name="keywords"
          content="Samsoftix, website development, app development, AI solutions, machine learning, SEO, e-commerce, digital marketing, cloud hosting, IT consulting, cybersecurity, Pakistan"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.samsoftix.com/all_services" />
        {/* Open Graph */}
        <meta property="og:title" content="Digital Services & Solutions | Samsoftix" />
        <meta property="og:description" content="Top digital services including website, app, AI/ML, SEO, e-commerce, cloud hosting, and IT consulting." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.samsoftix.com/all_services" />
        <meta property="og:site_name" content="Samsoftix" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Digital Services & Solutions | Samsoftix" />
        <meta name="twitter:description" content="Top digital services including website, app, AI/ML, SEO, e-commerce, cloud hosting, and IT consulting." />
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <header className="services-hero fade-section" style={{opacity: 0, transform: 'translateY(20px)'}}>
        <h1 style={{color:'#33ccff'}}> Professional Digital Services by SamSoftix</h1>
        <p>Empowering businesses with websites, apps, AI/ML solutions, SEO, e-commerce, and IT consulting for global growth.</p>
        <p className="global-note">🌍 Serving clients worldwide 100% remote, flexible, and borderless services.</p>
        <button className="book-btn" onClick={handleBookingClick}>Book Your Service Now</button>
      </header>

      {/* Remote Section */}
      <section className="remote-section fade-section" style={{opacity: 0, transform: 'translateY(20px)'}}>
        <h2>Remote, Flexible Digital Solutions</h2>
        <p>Collaborate with our expert team online. Track progress in real-time and receive professional guidance without leaving your office.</p>
      </section>

      {/* Services List */}
      <section className="services-list fade-section" style={{opacity: 0, transform: 'translateY(20px)'}}>
        <h2>Our Key Services</h2>
        {servicesData.map((service, index) => (
          <article key={index} className="service-box">
            <h3>{service.title}</h3>
            <p>{service.desc}</p>
          </article>
        ))}
      </section>

      {/* Booking CTA Section */}
      <section className="book-service-section fade-section" style={{opacity: 0, transform: 'translateY(20px)'}}>
        <h2>Start Your Digital Growth Today</h2>
        <p>Partner with Samsoftix for scalable, professional, and results-driven digital solutions tailored to your business needs.</p>
        <button className="book-btn" onClick={handleBookingClick}>Book Your Service Now</button>
      </section>
    </main>
  );
}

export default Services;
